/**
 * The Lessons tab's search state (lesson-redo DESIGN.md C2), one copy for both
 * fields: the head's and the feed bottom's (community-feed-invite DESIGN.md
 * §3.4). Moved out of LessonsTab.vue unchanged.
 *
 * 2 or more characters, 200ms, `searchAll(q, { page, pageSize: 9 })`; Enter
 * logs the phrase (the search log hears Enter, a lesson tap and a Mystic tap,
 * never a keystroke). `lesson_search` says how the search came: typed
 * (`search`), a topic (`topic`), or the feed's bottom (`foot`, always).
 */
import { computed, onUnmounted, ref, watch } from 'vue'
import { searchAll, logSearch } from '@/api-service'
import { track } from '@/utils/analytics'
import { TOPICS, resultLine, askerOf } from '@/utils/community-lessons'

export const SEARCH_PAGE = 9
export const SEARCH_DEBOUNCE = 200

export function useLessonSearch({ via: fixedVia = '' } = {}) {
  const query = ref('')
  const q = computed(() => query.value.trim())
  const searching = computed(() => q.value.length >= 2)

  const blank = () => ({ q: '', lessons: [], mystics: [], count: 0, topic: '', how: '', hasMore: false, page: 1, ask: null, loading: false, failed: false, done: false })
  const results = ref(blank())
  let seq = 0
  // How the search came (lesson_search via): typed, or a topic (the Topics line, the fire's stat).
  let via = 'search'
  function setVia(v) { via = v }
  async function runSearch(reset = true) {
    const phrase = q.value
    if (phrase.length < 2) return
    const r = results.value
    const page = reset ? 1 : r.page + 1
    const mine = ++seq
    r.loading = true
    r.failed = false
    if (reset) r.done = false
    const data = await searchAll(phrase, { page, pageSize: SEARCH_PAGE })
    if (mine !== seq) return   // a newer search has taken over
    if (data) {
      const lessons = Array.isArray(data.lessons) ? data.lessons : []
      results.value = {
        q: phrase,
        lessons: reset ? lessons : r.lessons.concat(lessons),
        mystics: reset || !r.mystics.length ? (Array.isArray(data.mystics) ? data.mystics.slice(0, 3) : []) : r.mystics,
        count: Number(data.count ?? lessons.length) || 0,
        topic: data.topic || '',
        how: data.how || '',
        hasMore: !!data.has_more,
        page,
        ask: data.ask || null,
        loading: false, failed: false, done: true,
      }
      if (reset) track('lesson_search', { via: fixedVia || via, topic: data.topic || 'none', how: data.how || 'none', found: Number(data.count) || 0 })
      via = 'search'
    } else {
      r.loading = false
      r.failed = true
      r.done = true
    }
  }
  const noMatch = computed(() => searching.value && results.value.done && !results.value.failed && !results.value.count && !results.value.lessons.length)
  const line = computed(() => resultLine({ count: results.value.count, topic: results.value.topic, query: results.value.q || q.value, mystics: results.value.mystics.length }))
  const asker = computed(() => {
    const a = results.value.ask
    return a && a.sub_domain ? { sub: a.sub_domain, name: a.name || 'Mystic Aida' } : askerOf(results.value.mystics)
  })

  let timer = null
  watch(query, () => {
    if (timer) clearTimeout(timer)
    if (!searching.value) { seq++; results.value = blank(); return }
    timer = setTimeout(() => runSearch(true), SEARCH_DEBOUNCE)
  })
  function now() { if (timer) clearTimeout(timer); if (searching.value) runSearch(true) }
  // Enter: the phrase counts (API.md §2) and is kept on this device (LessonSearch).
  function onSubmit(phrase) {
    if (String(phrase || '').trim().length >= 2) logSearch({ q: phrase, kind: 'enter' })
    now()
  }
  function onPick(phrase) {
    via = TOPICS.some((t) => t.label === phrase) ? 'topic' : 'search'
    now()
  }
  function clear() { query.value = '' }
  onUnmounted(() => { if (timer) clearTimeout(timer) })

  return { query, q, searching, results, runSearch, noMatch, line, asker, onSubmit, onPick, clear, setVia }
}
